import type {
  ChainQuorum,
  ChainStepKind,
  FillMethod,
  FormRecordFlag,
  RecordFileSource,
  RecordStatusView,
  RevisionSource,
  TemplateRole,
} from '@felix/domain';

/**
 * ISM form kayıtlarının arayüz tipleri (plan M8b Task 15; ADR 0042): gemi sunucusunun
 * `GET /api/ship/v1/form-records…` ve bulutun `GET /api/office/form-records…` yanıtlarıyla aynı
 * biçim (`packages/db` `form-record-views.ts`). Durum saklanmaz, okuma modelinden gelir.
 */

/** Bekleyen adım (karar ekranı, zil ve pano; ADR 0042 §16). */
export interface FormRecordPending {
  submissionId: string;
  step: number;
  steps: number;
  /** Adım ofiste (ADR 0026 §3). */
  office: boolean;
  approverRoleId: string;
  /** İmzası beklenen roller (ADR 0071 §4); eski sunucuda yok. */
  approverRoleIds?: string[];
  /** Onayın bağlanacağı içerik hash'i (gönderimin). */
  contentHash: string;
  submittedBy: string;
  approvedBy: string[];
  selfApproval: boolean;
  /** Beklemenin başladığı an (gönderim ya da önceki adımın onayı). */
  since: string;
  deadlineHours: number | null;
  /** Zincirin bütün adımları (ADR 0069); eski gemi sunucusunda yok. */
  chain?: { roleId: string; roleIds?: string[]; office: boolean; kind?: ChainStepKind }[];
}

/** Adımın bir kararı (imza). */
export interface ApprovalChainDecision {
  approverId: string;
  approverRoleId: string;
  decision: 'approved' | 'returned';
  reason: string | null;
  at: string;
  contentHash: string;
  /** Kararın anındaki gemi saati farkı (dakika). */
  shipOffsetMin?: number;
}

/**
 * Onay zincirinin bir adımı (adım göstergesi ve imza bloğu; ADR 0069): rol, yer ve bu gönderimdeki
 * kararı (yoksa `null`).
 */
export interface ApprovalChainStep {
  step: number;
  roleId: string;
  /** Adımın rolleri (ADR 0071 §4); eski sunucuda yok (`[roleId]`). */
  roleIds?: string[];
  /** "Hepsi" ya da "biri yeter" (ADR 0071 §2). */
  quorum?: ChainQuorum;
  /** Form zincirinde adımın türü: hazırlayan, ön onay, Master, ofis. */
  kind?: ChainStepKind;
  office: boolean;
  /** Adımın bu gönderimdeki kararları ("hepsi" adımında rol başına); eski sunucuda yok. */
  decisions?: ApprovalChainDecision[];
  decision: ApprovalChainDecision | null;
}

/** Form History satırı (`formRecordSummaries`). */
export interface FormRecordSummary {
  id: string;
  vesselId: string;
  recordNo: string;
  recordYear: number;
  recordSeq: number;
  title: string | null;
  docNo: string;
  documentTitle: string;
  revisionSource: RevisionSource;
  documentId: string;
  revisionId: string;
  revisionNo: string;
  fillMethod: FillMethod;
  /** Kaydın gemi yerel günü (`YYYY-MM-DD`). */
  recordDay: string;
  createdAt: string;
  createdBy: string;
  subjects: string[];
  status: RecordStatusView;
  overdue: boolean;
  pending: FormRecordPending | null;
  flags: FormRecordFlag[];
  draftRevisionState: string | null;
}

export interface FormRecordPage {
  items: FormRecordSummary[];
  total: number;
  page: number;
  pageSize: number;
}

/** Sunucunun sıralama anahtarları (ADR 0042 Ek U26); `-` önekli azalan. */
export const FORM_RECORD_SORT_KEYS = [
  'recordNo',
  'recordDay',
  'docNo',
  'title',
  'status',
  'createdAt',
] as const;
export type FormRecordSortKey = (typeof FORM_RECORD_SORT_KEYS)[number];
export type FormRecordSort = FormRecordSortKey | `-${FormRecordSortKey}`;

/** Form History süzgeci (ADR 0042 §21) [sorular #246]. */
export interface FormRecordQuery {
  status?: RecordStatusView['status'] | undefined;
  documentId?: string | undefined;
  /** Kayıt günü aralığı (gemi yerel günü). */
  from?: string | undefined;
  to?: string | undefined;
  flag?: FormRecordFlag | undefined;
  /** "Awaiting my approval". */
  awaiting?: 'me' | 'upcoming' | undefined;
  /** Yalnız ofiste: gemi (`?vessel=`). */
  vesselId?: string | undefined;
  /** Kategori ya da form konusu (`folder:<anahtar>`, `document:<id>`; ADR 0042 §8). */
  subject?: string | undefined;
  /** Yalnız değiştirilmiş kayıtlar. */
  amended?: boolean | undefined;
  /** Yalnız eski revizyon işaretli kayıtlar. */
  flagged?: boolean | undefined;
  /** Yalnız süresi geçmiş bekleyen adımı olanlar. */
  overdue?: boolean | undefined;
  sort?: FormRecordSort | undefined;
  page?: number | undefined;
  pageSize?: number | undefined;
}

export interface FormRecordApproval {
  step: number;
  decision: 'approved' | 'returned';
  reason: string | null;
  approverId: string;
  approverRoleId: string;
  at: string;
  office: boolean;
  approverRoleUnverified: boolean;
  /** Kararın matris sürümü ve bağlandığı içerik hash'i (kapak; ADR 0042 §20). */
  matrixVersion?: number;
  contentHash?: string;
}

/** Ofiste dosyanın durumu (ADR 0042 Ek U30; ADR 0038 #164). */
export type OfficeFileState = 'verified' | 'uploading' | 'on_vessel' | 'rejected';

export interface FormRecordFile {
  /** `record_revision_files` satırı (ofis indirmesi bu kimlikle). */
  id: string;
  sortOrder: number;
  name: string;
  sha256: string;
  size: number;
  mimeType: string;
  source: RecordFileSource;
  /** Gemide yükleme kuyruğunun, bulutta `_ship_files`'ın durumu. */
  transfer: string | null;
  /** Yalnız ofiste. */
  officeState?: OfficeFileState;
  /** Yalnız ofiste: bulutun aldığı bayt (yüzde; ADR 0038 §14). */
  bytesReceived?: number;
}

export interface FormRecordRevision {
  revisionNo: number;
  at: string;
  by: string;
  reason: string | null;
  note: string | null;
  contentHash: string;
  /** Revizyonun dosyaları; önceki revizyonların dosyaları görünür kalır (mimari belge §7.4). */
  files: Omit<FormRecordFile, 'transfer' | 'officeState' | 'bytesReceived'>[];
}

/** Taslak dosyası (`form_records.draft_files`; ADR 0042 §1); istek biçimiyle aynı. */
export interface DraftFile {
  sortOrder: number;
  name: string;
  sha256: string;
  size: number;
  mimeType: string;
  source: RecordFileSource;
}

/** Kişinin bu kayıttaki işlemleri (sunucu her istekte yeniden denetler). */
export interface FormRecordCan {
  editDraft?: boolean;
  discard?: boolean;
  submit?: boolean;
  amend?: boolean;
  resubmit?: boolean;
  decide: boolean;
  approve: boolean;
  return: boolean;
  void?: boolean;
  export: boolean;
  /**
   * Kişinin bu kayıtta sırası gelmemiş adımı (ADR 0069): Approve pasif ve "Waiting for <rol>"
   * yazılı. Yoksa `null` ya da yok.
   */
  upcomingStep?: number | null;
}

/** Kayıt sayfası (`GET …/form-records/:id`). */
export interface FormRecordDetail {
  id: string;
  vesselId: string;
  recordNo: string;
  title: string | null;
  docNo: string;
  documentTitle: string;
  revisionSource: RevisionSource;
  documentId: string;
  revisionId: string;
  revisionNo: string;
  /** Bağlı revizyonun içerik hash'i ve basılan şablon (kapak; ADR 0042 §2, §20). */
  templateContentHash?: string;
  templateSha256?: string;
  templateRole?: TemplateRole;
  fillMethod: FillMethod;
  recordDay: string;
  createdAt: string;
  createdBy: string;
  status: RecordStatusView;
  overdue: boolean;
  pendingSince: string | null;
  pending: FormRecordPending | null;
  subjects: string[];
  flags: FormRecordFlag[];
  /** ✓ gemide kaydedildi / ✓✓ ofise ulaştı (mimari belge §6.4). */
  delivery: 'ship' | 'office';
  revisions: FormRecordRevision[];
  approvals: FormRecordApproval[];
  /** Son gönderimin onay zinciri (ADR 0069); eski sunucuda yok. */
  chain?: ApprovalChainStep[];
  /** Son gönderimin hazırlayan imzası (ADR 0071 §3); form zincirinde rütbesiyle. */
  prepared?: { by: string; roleId: string | null; at: string } | null;
  files: FormRecordFile[];
  void: { at: string; by: string; reason: string } | null;
  /** Gemide taslak alanları (yalnız taslakta). */
  draft?: {
    title: string | null;
    note: string | null;
    files: DraftFile[];
    rowVersion: number;
  } | null;
  paperRevisionReason?: string | null;
  /** Ofiste kaydın gemisi. */
  vessel?: { id: string; name: string } | null;
  /** Ofiste geminin yükleme kuyruğu durumu (`window`: yükleme penceresi bekleniyor). */
  uploadState?: string | null;
  can: FormRecordCan;
  /** Kişi adları (kimlik → ad). */
  names: Record<string, string>;
  /** Rol adları (kimlik → ad): bekleyen adımın, onayların ve girenin rolü. */
  roles?: Record<string, string>;
  /** Kaydı girenin kayıt günündeki gemi rolleri (unvan; MEPC.312(74) 6.3). */
  enteredByRoleIds?: string[];
}

/** Şablonun bir dosyası (`formTemplateInfo`). */
export interface FormTemplateFile {
  name: string;
  sha256: string;
  size: number;
  kind: string;
  mimeType: string;
  /** Gemide doğrulanmış mı. */
  onBoard: boolean;
}

/** Bir yolun açıklığı ve kapalıysa nedeni (hata kodu). */
export interface TemplateGate {
  allowed: boolean;
  reason: string | null;
}

/**
 * Şablonun kayıt yolları (`GET /api/ship/v1/form-templates/:source/:revisionId`; ADR 0042 §3,
 * §18–§20): Open Form, Print Form, Fill in app adayı, New record.
 */
export interface FormTemplate {
  revisionSource: RevisionSource;
  revisionId: string;
  documentId: string;
  documentKind: string;
  docNo: string;
  title: string;
  revisionNo: string;
  current: boolean;
  everEffective: boolean;
  source: FormTemplateFile;
  printRendition: FormTemplateFile | null;
  openForm: TemplateGate;
  printForm: TemplateGate & { templateRole: TemplateRole; templateSha256: string };
  /** Yalnız aday: alan, XFA ve betik denetimi istemcide (ADR 0042 §19, Ek U32). */
  fillInApp: TemplateGate;
  newRecord: { allowed: boolean; paperException: boolean };
}

/** Taslak açma (`POST /api/ship/v1/form-records`). */
export interface OpenRecordInput {
  documentId: string;
  revisionSource: RevisionSource;
  /** Verilmezse yürürlükteki revizyon; kâğıt istisnasında eski revizyon. */
  revisionId?: string;
  fillMethod: FillMethod;
  title?: string | null;
  paperRevisionReason?: string | null;
}

/** Karar: ekranda görülen gönderim, adım ve içerik hash'i (ADR 0042 Ek U25). */
export interface RecordDecisionInput {
  decision: 'approved' | 'returned';
  reason?: string | null;
  /** Parola isteğe bağlı (ISM formu; ADR 0056): yalnız girildiyse. */
  password?: string;
  expected: { submissionId: string; step: number; contentHash: string };
}

/** Kayıt dosyası yüklemesinin yanıtı (`PUT …/document-uploads?purpose=record`). */
export interface StoredRecordFile {
  name: string;
  sha256: string;
  size: number;
  kind: string;
  mimeType: string;
  /** Görüntüde `LOW_RESOLUTION` (ret yok; ADR 0042 §6). */
  warnings: string[];
}

/** Zilin kayıt maddeleri (`GET …/form-records/bell`; ADR 0042 Ek U33). */
export interface FormRecordBellItem {
  kind: 'approval_pending' | 'approval_overdue';
  recordType: 'ism_form';
  recordId: string;
  recordNo: string;
  title: string | null;
  docNo: string;
  documentTitle: string;
  vesselId: string;
  submissionId: string;
  step: number;
  steps: number;
  contentHash: string;
  since: string;
  overdue: boolean;
}

export type FormRecordBellNotice = DraftRevisionNotice | FormReturnedNotice;

export interface DraftRevisionNotice {
  kind: 'draft_revision_not_current';
  recordId: string;
  recordNo: string;
  docNo: string;
  documentTitle: string;
  revisionNo: string;
  state: string | null;
}

/**
 * Kişiye dönen iade (ortak 2026-10-22; ADR 0116 §1): zil bildirimi ve panoda "Returned to you"
 * (`DashboardSummary.returnedToMe`).
 */
export interface FormReturnedNotice {
  kind: 'record_returned';
  recordId: string;
  recordNo: string;
  docNo: string;
  documentTitle: string;
  vesselId: string;
  returnedAt: string;
  returnedBy: string;
  returnedRole: string | null;
  reason: string | null;
  office: boolean;
}

export interface FormRecordBell {
  count: number;
  items: FormRecordBellItem[];
  notices: FormRecordBellNotice[];
}

/** Pano "Pending approvals" kartı (`DashboardSummary.approvals`; ADR 0042 Ek U29). */
export interface ApprovalAlert {
  kind: 'approval_pending' | 'approval_overdue';
  recordType: 'ism_form';
  recordId: string;
  recordNo: string;
  title: string | null;
  docNo: string;
  documentTitle: string;
  vesselId: string;
  submissionId: string;
  step: number;
  steps: number;
  office: boolean;
  approverRoleId: string;
  contentHash: string;
  since: string;
  deadlineHours: number | null;
}

/** Kişiyi zincirde bekleyen, sırası gelmemiş kayıt (gri "Upcoming" satırı; ADR 0069). */
export interface ApprovalUpcoming {
  recordType: 'ism_form';
  recordId: string;
  recordNo: string;
  title: string | null;
  docNo: string;
  documentTitle: string;
  vesselId: string;
  step: number;
  steps: number;
  waitingStep: number;
  waitingRoleId: string;
  waitingRole: string | null;
  since: string;
}

export interface ApprovalCard {
  pending: number;
  overdue: number;
  items: ApprovalAlert[];
  /** Eski sunucuda yok. */
  upcoming?: ApprovalUpcoming[];
}

/**
 * ISM ağacında formun kayıtları (`GET …/form-records/tree`; ADR 0072): son 5 kayıt ve bütün
 * bekleyenler, yeniden eskiye; "All (n)" için toplam.
 */
export interface FormRecordTreeItem {
  id: string;
  recordNo: string;
  /** Kaydın gemi yerel günü (`YYYY-MM-DD`). */
  recordDay: string;
  title: string | null;
  /** Durum; `revisionNo` kaydın kendi revizyonudur (değişiklik sayısı). */
  status: RecordStatusView;
  /** Formun (şablonun) revizyon no'su; ağaçta "Rev.2" bu (ADR 0107 §3). Eski sunucuda yok. */
  formRevisionNo?: string;
  overdue: boolean;
  pending: {
    step: number;
    steps: number;
    office: boolean;
    approverRoleId: string;
    /** İmzası beklenen bütün rütbeler (ADR 0071 §4); eski sunucuda yok. */
    approverRoleIds?: string[];
  } | null;
}

export interface FormRecordTreeView {
  documentId: string;
  total: number;
  items: FormRecordTreeItem[];
  /** Bekleyen adımların rol adları (kimlik → ad). */
  roles: Record<string, string>;
}
